import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { muatArchV2, kompilPrompt, janaUjian, binaPilihan, binaProduk, gambarProduk } from '../services/promptArchV2Service';
import ArchV2Playground from './ArchV2Playground';
import ArchV2Pustaka from './ArchV2Pustaka';
import ArchV2Inspector from './ArchV2Inspector';
import ArchV2Sejarah from './ArchV2Sejarah';

// ═══════════════════════════════════════════════════════════
//  UJIAN PROMPT ARCHITECTURE V2 — makmal /pipeline-test, tab ketiga (2026-10-09)
//  Admin sahaja. Uji seni bina master prompt MODULAR:
//    Global Master → Product → Category Playbook → Story Formula → Presentation Style
//    → Creative Preset → Scene Planner (1–5 scene) → Video Model Adapter → Output Contract.
//
//  ⭐ TERASING daripada production: tidak menyentuh page Generate pelanggan, master prompt
//    `modules/*`, kuota, produk pelanggan atau extension. Semua kerja di server melalui SATU
//    callable `promptArchV2` (functions/promptArchV2*.js).
//
//  ⭐ Model OpenAI: senarai datang dari server (senarai putih makmal "Ujian Master Prompt");
//    `labModels` + `normalizeReasoningFor` diserah oleh PipelineTestView (induk) supaya label
//    dan peraturan tahap reasoning = SAMA dengan tab itu. Jangan tambah senarai model di sini.
//
//  Tetapan Playground disimpan per-browser (localStorage) — dipulih melalui useState(() => …),
//  BUKAN effect "pulih" (perangkap StrictMode yang sedia ada pada tab lain page ini).
// ═══════════════════════════════════════════════════════════

const KUNCI_SIMPAN = 'archV2_state_v1';
const TAB = [['playground', 'Playground'], ['pustaka', 'Prompt Library'], ['inspector', 'Prompt Inspector'], ['sejarah', 'Test History']];
const TETAPAN_LALAI = {
  sumberProduk: 'pustaka', kategoriId: '', ceritaId: '', gayaId: '', presetId: '', adapterId: '',
  bilBabak: 3, tempoh: 8, model: [], reasoning: 'low', verbosity: 'low', maxTokens: 8000,
  vision: false, // Vision: hantar gambar produk terus kepada AI penulis prompt (2026-10-15)
};

function bacaSimpanan() {
  try {
    const s = JSON.parse(localStorage.getItem(KUNCI_SIMPAN) || 'null');
    return s && typeof s === 'object' ? s : {};
  } catch {
    return {};
  }
}

/** Isi pilihan yang kosong / sudah tidak wujud dengan lalai yang sah daripada pustaka semasa. */
function lengkapkan(t, lib, labModels) {
  const p = lib.pustaka;
  const ada = (jenis, id) => !!id && p[jenis].some((e) => e.id === id);
  const pertama = (jenis) => (p[jenis].find((e) => e.aktif && !e.diarkib) || p[jenis][0])?.id || '';
  const keluar = { ...TETAPAN_LALAI, ...t };
  if (!ada('kategori', keluar.kategoriId)) keluar.kategoriId = pertama('kategori');
  if (!ada('preset', keluar.presetId)) keluar.presetId = '';
  if (!ada('cerita', keluar.ceritaId)) keluar.ceritaId = pertama('cerita');
  if (!ada('gaya', keluar.gayaId)) keluar.gayaId = pertama('gaya');
  if (!ada('adapter', keluar.adapterId)) keluar.adapterId = pertama('adapter');
  const bil = Number(keluar.bilBabak);
  keluar.bilBabak = Number.isInteger(bil) && bil >= lib.had.babakMin && bil <= lib.had.babakMaks ? bil : 3;
  const dibenarkan = p.adapter.find((e) => e.id === keluar.adapterId)?.tempohDibenarkan || [];
  if (!dibenarkan.includes(Number(keluar.tempoh))) {
    const lalai = p.babak.find((e) => e.id === lib.idTunggal)?.tempohLalai;
    keluar.tempoh = dibenarkan.includes(lalai) ? lalai : (dibenarkan[dibenarkan.length - 1] ?? Number(keluar.tempoh));
  } else {
    keluar.tempoh = Number(keluar.tempoh);
  }
  const idServer = lib.model.map((m) => m.id);
  const model = (Array.isArray(keluar.model) ? keluar.model : []).filter((m) => idServer.includes(m));
  if (!model.length) {
    const lalai = (labModels || []).map((l) => l.id).find((id) => idServer.includes(id)) || idServer[0];
    if (lalai) model.push(lalai);
  }
  keluar.model = model;
  if (!lib.had.verbosity.includes(keluar.verbosity)) keluar.verbosity = lib.had.verbosity[0];
  return keluar;
}

export default function ArchV2Makmal({ produkPustaka, onPilihProduk, labModels, normalizeReasoningFor }) {
  const [simpanan] = useState(bacaSimpanan);
  const [tab, setTab] = useState(() => (TAB.some(([id]) => id === simpanan.tab) ? simpanan.tab : 'playground'));
  const [tetapanMentah, setTetapanMentah] = useState(() => ({ ...TETAPAN_LALAI, ...(simpanan.tetapan || {}) }));
  const [tambahan, setTambahan] = useState(() => ({ ...(simpanan.tambahan || {}) }));

  const [lib, setLib] = useState(null);
  const [libVersi, setLibVersi] = useState(0);
  const [libSedang, setLibSedang] = useState(true);
  const [libRalat, setLibRalat] = useState(null);

  const [hasil, setHasil] = useState([]);
  const [sedangJana, setSedangJana] = useState(false);
  const [kompilan, setKompilan] = useState({ data: null, kunci: null, sedang: false, ralat: null });
  const [segar, setSegar] = useState(0);
  const [notis, setNotis] = useState(null);

  const muat = useCallback(async () => {
    try {
      const r = await muatArchV2();
      if (!r?.pustaka || !r?.jenis || !Array.isArray(r?.model)) throw new Error('Server memulangkan pustaka yang tidak lengkap.');
      setLib(r);
      setLibVersi((n) => n + 1);
      setLibRalat(null);
      return r;
    } catch (e) {
      console.error('[ArchV2] muat pustaka gagal:', e);
      setLibRalat(e?.message || String(e));
      return null;
    } finally {
      setLibSedang(false);
    }
  }, []);

  useEffect(() => { muat(); }, [muat]);

  useEffect(() => {
    try {
      localStorage.setItem(KUNCI_SIMPAN, JSON.stringify({ tab, tetapan: tetapanMentah, tambahan }));
    } catch { /* storan penuh / disekat — tetapan sekadar tidak diingat */ }
  }, [tab, tetapanMentah, tambahan]);

  const t = useMemo(() => (lib ? lengkapkan(tetapanMentah, lib, labModels) : tetapanMentah), [tetapanMentah, lib, labModels]);
  const ubah = useCallback((tampal) => setTetapanMentah((p) => ({ ...p, ...tampal })), []);
  const ubahTambahan = useCallback((tampal) => setTambahan((p) => ({ ...p, ...tampal })), []);

  const pilihan = useMemo(() => binaPilihan(t), [t]);
  const produk = useMemo(() => binaProduk({ sumber: t.sumberProduk, pustaka: produkPustaka, tambahan }), [t.sumberProduk, produkPustaka, tambahan]);
  const vision = t.vision === true;
  // Vision: library sahaja (mod Manual tiada gambar). Gambar rosak/tiada → Generate dikunci dengan sebab.
  const gambar = useMemo(() => (vision ? gambarProduk(t.sumberProduk === 'manual' ? null : produkPustaka) : null), [vision, t.sumberProduk, produkPustaka]);
  const kunciSemasa = useMemo(() => JSON.stringify([libVersi, pilihan, produk, vision]), [libVersi, pilihan, produk, vision]);

  const reasoningBagi = (m) => {
    const tahap = lib?.model.find((x) => x.id === m)?.reasoning || [];
    const lv = typeof normalizeReasoningFor === 'function' ? normalizeReasoningFor(m, t.reasoning) : t.reasoning;
    // Server = hakim terakhir tahap yang sah; kalau kembar di page ini tertinggal, jatuh ke 'low'.
    return tahap.includes(lv) ? lv : (tahap.includes('low') ? 'low' : (tahap[0] || lv));
  };

  const halanganProduk = !lib ? 'Pustaka belum dimuat.'
    : (t.sumberProduk === 'manual'
      ? (String(tambahan.nama || '').trim() ? null : 'Isi nama produk dahulu.')
      : (produkPustaka ? null : 'Pilih produk dari library dahulu (atau tukar ke Manual).'));
  const tokenN = Number(t.maxTokens);
  const halanganJana = halanganProduk
    || (vision && t.sumberProduk === 'manual' ? 'Vision perlukan produk dari library (mod Manual tiada gambar) — matikan Vision atau tukar ke Library product.' : null)
    || (vision && gambar?.ralat ? `Vision: ${gambar.ralat}` : null)
    || (!t.model.length ? 'Tanda sekurang-kurangnya satu model.' : null)
    || (lib && (!Number.isInteger(tokenN) || tokenN < lib.had.tokenMin || tokenN > lib.had.tokenMaks)
      ? `Max output tokens mesti nombor bulat ${lib.had.tokenMin}–${lib.had.tokenMaks}.` : null);

  const kompilSekarang = async () => {
    if (halanganProduk || kompilan.sedang) return;
    const kunci = kunciSemasa;
    setKompilan((k) => ({ ...k, sedang: true, ralat: null }));
    try {
      const data = await kompilPrompt({ pilihan, produk, vision });
      setKompilan({ data, kunci, sedang: false, ralat: null });
    } catch (e) {
      setKompilan((k) => ({ ...k, sedang: false, ralat: e?.message || String(e) }));
    }
  };

  const jana = async () => {
    if (sedangJana || halanganJana) return;
    const model = [...t.model];
    const kumpulan = 'k' + Date.now().toString(36);
    setNotis(null);
    setSedangJana(true);
    setHasil(model.map((m) => ({ _model: m, _sedang: true })));
    // Satu klik → semua model yang ditanda, serentak. Setiap satu = run sendiri dalam Test History.
    await Promise.all(model.map(async (m, i) => {
      let r;
      try {
        r = await janaUjian({
          pilihan, produk, model: m, reasoning: reasoningBagi(m), verbosity: t.verbosity, maxTokens: tokenN, kumpulan,
          vision, gambarUrl: gambar?.url || '',
        });
      } catch (e) {
        console.error(`[ArchV2] jana ${m} gagal:`, e);
        r = { _model: m, _ralatPanggilan: e?.message || String(e) };
      }
      setHasil((h) => h.map((x, j) => (j === i ? r : x)));
    }));
    setSedangJana(false);
    setSegar((n) => n + 1);
  };

  const gunaSemula = (run) => {
    const p = run?.pilihan || {};
    ubah({
      kategoriId: p.kategoriId || '', presetId: p.presetId || '', ceritaId: p.ceritaId || '', gayaId: p.gayaId || '',
      adapterId: p.adapterId || '', bilBabak: p.bilBabak, tempoh: p.tempoh,
      model: run?.model ? [run.model] : [], reasoning: run?.reasoning || 'low',
      verbosity: run?.verbosity || 'low', maxTokens: run?.maxTokens || 8000,
      vision: run?.vision === true,
    });
    setTab('playground');
    setNotis(`Setting run "${run?.namaProduk || ''}" dimuat ke Playground. Produk TIDAK ditukar — pilih produk yang sama kalau mahu mengulang run itu dengan tepat.`);
  };

  const nilaiDisimpan = (id, nilai, catatan) => {
    setHasil((h) => h.map((r) => (r.id === id ? { ...r, nilai, catatan } : r)));
    setSegar((n) => n + 1);
  };

  return (
    <div>
      <div className="rounded-xl bg-white border border-slate-200 p-5 mb-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="text-[16px] font-bold text-slate-800 m-0">Ujian Prompt Architecture V2</p>
            <p className="text-[12.5px] text-slate-500 leading-relaxed m-0 mt-1 max-w-3xl">
              Master prompt modular: Global → Product → Category → Story Formula → Presentation Style → Preset → Scene Planner → Video Adapter → Output Contract.
              Eksperimen sahaja — <b>tidak menyentuh</b> page Generate pelanggan, master prompt production, kuota atau extension.
            </p>
          </div>
          <button
            onClick={() => { setLibSedang(true); muat(); }}
            disabled={libSedang}
            className="h-8 px-3 text-[12px] font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40"
          >{libSedang ? 'Memuat…' : 'Reload library'}</button>
        </div>
        <div className="flex gap-1 mt-4 flex-wrap">
          {TAB.map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`px-3.5 py-1.5 text-[13px] font-semibold rounded-lg ${tab === id ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:bg-slate-50'}`}
            >{label}</button>
          ))}
        </div>
      </div>

      {libRalat && (
        <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 mb-4 text-[13px] text-rose-700">
          <p className="m-0"><span className="font-bold">Pustaka prompt gagal dimuat:</span> {libRalat}</p>
          <p className="text-[12px] m-0 mt-1">Kalau mesej menyebut "not-found" atau "internal" tanpa butiran, function server <span className="font-mono">promptArchV2</span> belum di-deploy.{lib ? ' Paparan di bawah ialah salinan terakhir yang berjaya dimuat.' : ''}</p>
        </div>
      )}
      {!lib && libSedang && (
        <div className="rounded-xl bg-white border border-slate-200 p-8 text-center">
          <span className="material-symbols-outlined text-[28px] text-indigo-500 animate-spin">progressive_activity</span>
          <p className="text-[13px] text-slate-500 m-0 mt-2">Memuatkan pustaka prompt…</p>
        </div>
      )}
      {notis && (
        <div className="rounded-xl bg-indigo-50 border border-indigo-100 px-4 py-3 mb-4 text-[12.5px] text-indigo-700 flex items-start justify-between gap-3">
          <span>{notis}</span>
          <button onClick={() => setNotis(null)} className="font-semibold hover:underline whitespace-nowrap">Tutup</button>
        </div>
      )}

      {/* Semua tab kekal terpasang (disorok, bukan dibuang) supaya suntingan pustaka yang belum
          disimpan dan run yang sedang berjalan tidak hilang bila admin menukar tab. */}
      {lib && (
        <>
          <div className={tab === 'playground' ? '' : 'hidden'}>
            <ArchV2Playground
              lib={lib} tetapan={t} ubah={ubah} tambahan={tambahan} ubahTambahan={ubahTambahan}
              produkPustaka={produkPustaka} onPilihProduk={onPilihProduk} labModels={labModels}
              reasoningBagi={reasoningBagi} gambar={gambar} onJana={jana} onInspect={() => { setTab('inspector'); kompilSekarang(); }}
              sedangJana={sedangJana} hasil={hasil} halangan={halanganJana} onNilaiDisimpan={nilaiDisimpan}
            />
          </div>
          <div className={tab === 'pustaka' ? '' : 'hidden'}>
            <ArchV2Pustaka lib={lib} onMuatSemula={muat} />
          </div>
          <div className={tab === 'inspector' ? '' : 'hidden'}>
            <ArchV2Inspector
              kompilan={{ ...kompilan, basi: kompilan.kunci != null && kompilan.kunci !== kunciSemasa }}
              onKompil={kompilSekarang} dasar={lib.dasar} halangan={halanganProduk}
            />
          </div>
          <div className={tab === 'sejarah' ? '' : 'hidden'}>
            <ArchV2Sejarah lib={lib} segar={segar} onGunaSemula={gunaSemula} />
          </div>
        </>
      )}
    </div>
  );
}
